import type { TooltipRenderProps } from 'react-joyride';

/** Branded tooltip: step counter, progress bar and clearly labelled actions, with dark-mode support. */
export default function TourTooltip({
  index,
  size,
  step,
  isLastStep,
  backProps,
  primaryProps,
  skipProps,
  closeProps,
  tooltipProps,
}: TooltipRenderProps) {
  const progress = Math.round(((index + 1) / size) * 100);

  return (
    <div
      {...tooltipProps}
      className="w-[min(92vw,380px)] overflow-hidden rounded-2xl border border-emerald-100 bg-white text-gray-900 shadow-2xl dark:border-white/10 dark:bg-slate-900 dark:text-gray-100"
    >
      <div className="h-1 w-full bg-emerald-100 dark:bg-white/10" aria-hidden="true">
        <div className="h-full bg-primary-100 transition-all duration-300" style={{ width: `${progress}%` }} />
      </div>

      <div className="p-5">
        <div className="mb-2 flex items-center justify-between gap-3">
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-100">
            Step {index + 1} of {size}
          </span>
          <button
            {...closeProps}
            type="button"
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/10 dark:hover:text-white"
            aria-label="Close the tour"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        {step.title ? <h2 className="mb-2 text-lg font-semibold leading-snug">{step.title}</h2> : null}
        <div className="text-sm leading-6 text-gray-700 dark:text-gray-300">{step.content}</div>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-gray-100 bg-gray-50/70 px-5 py-3 dark:border-white/10 dark:bg-white/5">
        <button
          {...skipProps}
          type="button"
          className="text-sm font-medium text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white"
        >
          {isLastStep ? 'Close' : 'Skip for now'}
        </button>
        <div className="flex items-center gap-2">
          {index > 0 ? (
            <button
              {...backProps}
              type="button"
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-white dark:border-white/15 dark:text-gray-200 dark:hover:bg-white/10"
            >
              Back
            </button>
          ) : null}
          <button
            {...primaryProps}
            type="button"
            className="rounded-lg bg-primary-100 px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-70"
          >
            {isLastStep ? 'Finish' : index === 0 ? 'Start tour' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}
